import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import * as z from "zod";

export enum ShippingClaimEventType {
  CREATED = "created",
  STATUS_CHANGED = "status_changed",
  PHOTOS_ADDED = "photos_added",
  FILED_WITH_CARRIER = "filed_with_carrier",
  REFUND_ISSUED = "refund_issued",
  CONVERTED_TO_USPS = "converted_to_usps",
}

const BaseShippingClaimEventSchema = z.object({
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  userId: zExt.objectId().nullish().meta({ privacy: privacy.nonPii }),
});

const CreatedEventSchema = BaseShippingClaimEventSchema.extend({
  type: z
    .literal(ShippingClaimEventType.CREATED)
    .meta({ privacy: privacy.nonPii }),
  status: z.string().meta({ privacy: privacy.nonPii }),
  proactive: z.boolean().nullish().meta({ privacy: privacy.nonPii }),
});

const StatusChangedEventSchema = BaseShippingClaimEventSchema.extend({
  type: z
    .literal(ShippingClaimEventType.STATUS_CHANGED)
    .meta({ privacy: privacy.nonPii }),
  fromStatus: z.string().meta({ privacy: privacy.nonPii }),
  toStatus: z.string().meta({ privacy: privacy.nonPii }),
});

const PhotosAddedEventSchema = BaseShippingClaimEventSchema.extend({
  type: z
    .literal(ShippingClaimEventType.PHOTOS_ADDED)
    .meta({ privacy: privacy.nonPii }),
  photoCount: z.number().meta({ privacy: privacy.nonPii }),
});

const FiledWithCarrierEventSchema = BaseShippingClaimEventSchema.extend({
  type: z
    .literal(ShippingClaimEventType.FILED_WITH_CARRIER)
    .meta({ privacy: privacy.nonPii }),
  carrierClaimNumber: z.string().nullish().meta({ privacy: privacy.nonPii }),
  carrier: z.string().nullish().meta({ privacy: privacy.nonPii }),
});

const RefundIssuedEventSchema = BaseShippingClaimEventSchema.extend({
  type: z
    .literal(ShippingClaimEventType.REFUND_ISSUED)
    .meta({ privacy: privacy.nonPii }),
  merchantRefundAmount: z.string().nullish().meta({ privacy: privacy.nonPii }),
});

// A UPS claim that UPS handed off to USPS for last-mile (E.C.LwsPackage) and we
// re-filed under USPS with the postal tracking number.
const ConvertedToUspsEventSchema = BaseShippingClaimEventSchema.extend({
  type: z
    .literal(ShippingClaimEventType.CONVERTED_TO_USPS)
    .meta({ privacy: privacy.nonPii }),
  fromCarrier: z.string().meta({ privacy: privacy.nonPii }),
  fromTrackingCode: z.string().meta({ privacy: privacy.id.tracking }),
  toTrackingCode: z.string().meta({ privacy: privacy.id.tracking }),
  reason: z.string().nullish().meta({ privacy: privacy.text.thirdParty }),
});

export const ShippingClaimEventSchema = z.discriminatedUnion("type", [
  CreatedEventSchema.meta({ privacy: privacy.structured }),
  StatusChangedEventSchema.meta({ privacy: privacy.structured }),
  PhotosAddedEventSchema.meta({ privacy: privacy.structured }),
  FiledWithCarrierEventSchema.meta({ privacy: privacy.structured }),
  RefundIssuedEventSchema.meta({ privacy: privacy.structured }),
  ConvertedToUspsEventSchema.meta({ privacy: privacy.structured }),
]);

export type ShippingClaimEvent = z.infer<typeof ShippingClaimEventSchema>;
